import {
  LEGACY_PRIMARY_NATIVE_API_BASE_URL,
  NATIVE_API_BASE_URL,
  PUBLIC_WEB_ORIGIN,
  RU_NATIVE_API_BASE_URL,
  RU_PUBLIC_WEB_ORIGIN,
  isNativeApp,
  setNativeTransportOrigins,
} from "./runtime";

const STORAGE_KEY = "vg_native_region_routing_v1";
const BOOTSTRAP_TIMEOUT_MS = 6_000;
const DIRECT_RU_TIMEOUT_MS = 4_000;
const PRIMARY_HEDGE_DELAY_MS = 600;

const DEFAULT_SNAPSHOT = Object.freeze({
  configVersion: 1,
  ruEdgeEnabled: false,
  accountRegion: null,
  registrationRegion: "EU",
  language: "en",
  country: null,
  networkCountry: null,
  city: "Bratislava",
  transport: "primary",
  apiOrigin: NATIVE_API_BASE_URL,
  publicOrigin: PUBLIC_WEB_ORIGIN,
});

let currentSnapshot = { ...DEFAULT_SNAPSHOT };
let initializationGeneration = 0;
let pendingSelectedCountry = "";

const normalizeRegion = (value, fallback = "EU") => (
  String(value || "").trim().toUpperCase() === "RU" ? "RU" : fallback
);

const normalizeCountry = (value) => {
  const country = String(value || "").trim().toUpperCase();
  return /^[A-Z]{2}$/.test(country) && !["XX", "T1", "ZZ"].includes(country)
    ? country
    : null;
};

const normalizeEuropeanLanguage = (value) => {
  const language = String(value || "en").trim().toLowerCase().split("-")[0];
  return language === "sk" || language === "cs" ? language : "en";
};

function normalizeBootstrapPayload(payload, requestedApiOrigin = null) {
  if (!payload || Number(payload.config_version) !== 1) return null;

  const transport = payload.transport === "ru_edge" ? "ru_edge" : "primary";
  const ruEdgeEnabled = payload.ru_edge_enabled === true;
  const expectedServerApiOrigins = transport === "ru_edge"
    ? [RU_NATIVE_API_BASE_URL, NATIVE_API_BASE_URL]
    : [LEGACY_PRIMARY_NATIVE_API_BASE_URL, NATIVE_API_BASE_URL];
  const expectedPublicOrigin = transport === "ru_edge"
    ? RU_PUBLIC_WEB_ORIGIN
    : PUBLIC_WEB_ORIGIN;
  const serverApiOrigin = String(payload.api_origin || "").replace(/\/+$/, "");
  const publicOrigin = String(payload.public_origin || "").replace(/\/+$/, "");

  if (!expectedServerApiOrigins.includes(serverApiOrigin) || publicOrigin !== expectedPublicOrigin) {
    return null;
  }
  if (transport === "ru_edge" && !ruEdgeEnabled) return null;

  const accountRegion = payload.account_region == null
    ? null
    : normalizeRegion(payload.account_region);
  const registrationRegion = normalizeRegion(payload.registration_region);
  if (accountRegion && accountRegion !== registrationRegion) return null;
  const networkCountry = normalizeCountry(payload.network_country);
  const storedClientApiOrigin = String(payload.client_api_origin || "").replace(/\/+$/, "");

  if (
    storedClientApiOrigin
    && ![NATIVE_API_BASE_URL, RU_NATIVE_API_BASE_URL].includes(storedClientApiOrigin)
  ) {
    return null;
  }

  let apiOrigin = NATIVE_API_BASE_URL;
  if (transport === "ru_edge") {
    const shouldUseDirectRu = (
      requestedApiOrigin === RU_NATIVE_API_BASE_URL
      || storedClientApiOrigin === RU_NATIVE_API_BASE_URL
      || networkCountry === "RU"
      || accountRegion === "RU"
      || registrationRegion === "RU"
    );
    apiOrigin = shouldUseDirectRu ? RU_NATIVE_API_BASE_URL : NATIVE_API_BASE_URL;
  }

  return {
    configVersion: 1,
    ruEdgeEnabled,
    accountRegion,
    registrationRegion,
    language: registrationRegion === "RU" ? "ru" : normalizeEuropeanLanguage(payload.language),
    country: normalizeCountry(payload.country),
    networkCountry,
    city: payload.city ? String(payload.city).trim() : null,
    transport,
    apiOrigin,
    // The edge is only a network path. Shared links must follow the account
    // contour even when an EU account reaches the API through the RU edge.
    publicOrigin: registrationRegion === "RU" ? RU_PUBLIC_WEB_ORIGIN : PUBLIC_WEB_ORIGIN,
  };
}

function persistPayload(payload, snapshot) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...payload,
      client_api_origin: snapshot.apiOrigin,
    }));
  } catch {
    // Routing remains valid for this launch even if WebView storage is full.
  }
}

function restoreStoredSnapshot() {
  try {
    const payload = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    const snapshot = normalizeBootstrapPayload(payload);
    if (!snapshot) return null;
    currentSnapshot = snapshot;
    setNativeTransportOrigins(snapshot);
    return snapshot;
  } catch {
    return null;
  }
}

function applyBootstrapPayload(payload, requestedApiOrigin = null) {
  const snapshot = normalizeBootstrapPayload(payload, requestedApiOrigin);
  if (!snapshot) throw new Error("Native bootstrap returned an invalid transport contract.");
  currentSnapshot = snapshot;
  setNativeTransportOrigins(snapshot);
  persistPayload(payload, snapshot);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("vg:native-routing-changed", { detail: snapshot }));
  }
  return snapshot;
}

async function requestBootstrap(
  apiOrigin,
  accessToken,
  registrationRegion = currentSnapshot.registrationRegion,
  timeoutMs = BOOTSTRAP_TIMEOUT_MS,
  selectedCountry = pendingSelectedCountry
) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const send = (token = null) => {
    const headers = {
      Accept: "application/json",
      "X-VaultGrid-Client": "native",
      "X-VaultGrid-Registration-Region": normalizeRegion(registrationRegion),
    };
    if (selectedCountry) headers["X-VaultGrid-Selected-Country"] = selectedCountry;
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`${apiOrigin}/accounts/native/bootstrap/`, {
      method: "GET",
      headers,
      cache: "no-store",
      signal: controller.signal,
    });
  };

  try {
    let response = await send(accessToken);
    // DRF authenticates before applying AllowAny, so an expired optional JWT
    // turns an otherwise public bootstrap into a 401.  Retry once as a guest;
    // a subsequently refreshed token will immediately re-bootstrap using the
    // authenticated account region.
    if (response.status === 401 && accessToken) {
      response = await send();
    }
    const contentType = String(response.headers?.get?.("content-type") || "").toLowerCase();
    if (!response.ok || !contentType.includes("application/json")) {
      throw new Error(`Native bootstrap unavailable (${response.status}).`);
    }
    return await response.json();
  } finally {
    clearTimeout(timeoutId);
  }
}

async function requestValidBootstrap(
  apiOrigin,
  accessToken,
  timeoutMs,
  registrationRegion = currentSnapshot.registrationRegion
) {
  const payload = await requestBootstrap(
    apiOrigin,
    accessToken,
    registrationRegion,
    timeoutMs
  );
  const snapshot = normalizeBootstrapPayload(payload, apiOrigin);
  if (!snapshot || (apiOrigin === RU_NATIVE_API_BASE_URL && snapshot.transport !== "ru_edge")) {
    throw new Error("Native bootstrap returned an invalid transport contract.");
  }
  return payload;
}

const settledBootstrap = (promise, apiOrigin) => promise
  .then((payload) => ({ ok: true, payload, apiOrigin }))
  .catch((error) => ({ ok: false, error, apiOrigin }));

const hasRussianRoutingSignal = (snapshot) => (
  snapshot?.networkCountry === "RU"
  || snapshot?.accountRegion === "RU"
  || snapshot?.registrationRegion === "RU"
);

function promoteBackgroundRuProbe(resultPromise, generation) {
  void resultPromise.then((result) => {
    if (!result.ok || generation !== initializationGeneration) return;
    const snapshot = normalizeBootstrapPayload(result.payload, result.apiOrigin);
    if (hasRussianRoutingSignal(snapshot)) {
      applyBootstrapPayload(result.payload, result.apiOrigin);
    }
  });
}

export function getNativeRoutingSnapshot() {
  return { ...currentSnapshot };
}

export const getNativeRegistrationRegion = () => currentSnapshot.registrationRegion;

export const isRussianNativeRoutingScenario = () => (
  currentSnapshot.accountRegion === "RU"
  || currentSnapshot.registrationRegion === "RU"
  || currentSnapshot.networkCountry === "RU"
  || currentSnapshot.transport === "ru_edge"
);

let russianRecheckPromise = null;

export async function recheckRussianNativeRegionRouting({ accessToken = null } = {}) {
  if (!isNativeApp() || !isRussianNativeRoutingScenario()) {
    return getNativeRoutingSnapshot();
  }
  if (russianRecheckPromise) return russianRecheckPromise;

  const apiOrigin = currentSnapshot.apiOrigin === RU_NATIVE_API_BASE_URL
    ? RU_NATIVE_API_BASE_URL
    : NATIVE_API_BASE_URL;
  russianRecheckPromise = requestBootstrap(
    apiOrigin,
    accessToken,
    currentSnapshot.registrationRegion,
    apiOrigin === RU_NATIVE_API_BASE_URL ? DIRECT_RU_TIMEOUT_MS : BOOTSTRAP_TIMEOUT_MS
  )
    .then((payload) => applyBootstrapPayload(payload, apiOrigin))
    .catch(() => getNativeRoutingSnapshot())
    .finally(() => {
      russianRecheckPromise = null;
    });
  return russianRecheckPromise;
}

let primaryFailureRecoveryPromise = null;

export async function recoverNativeRegionRoutingAfterPrimaryFailure({
  accessToken = null,
} = {}) {
  if (!isNativeApp()) return getNativeRoutingSnapshot();
  if (primaryFailureRecoveryPromise) return primaryFailureRecoveryPromise;

  primaryFailureRecoveryPromise = requestValidBootstrap(
    RU_NATIVE_API_BASE_URL,
    accessToken,
    DIRECT_RU_TIMEOUT_MS
  )
    .then((payload) => applyBootstrapPayload(payload, RU_NATIVE_API_BASE_URL))
    .catch(() => getNativeRoutingSnapshot())
    .finally(() => {
      primaryFailureRecoveryPromise = null;
    });
  return primaryFailureRecoveryPromise;
}

export async function activateNativeRussianRegistrationRoute() {
  if (!isNativeApp()) return getNativeRoutingSnapshot();
  const payload = await requestBootstrap(
    RU_NATIVE_API_BASE_URL,
    null,
    "RU",
    DIRECT_RU_TIMEOUT_MS
  );
  return applyBootstrapPayload(payload, RU_NATIVE_API_BASE_URL);
}

export async function activateNativeRegistrationRoute(country) {
  if (!isNativeApp()) return getNativeRoutingSnapshot();
  const selectedCountry = String(country || "").trim().toUpperCase();
  pendingSelectedCountry = selectedCountry;
  const generation = ++initializationGeneration;
  const russian = selectedCountry === "RU";
  const apiOrigin = russian ? RU_NATIVE_API_BASE_URL : NATIVE_API_BASE_URL;
  const payload = await requestBootstrap(
    apiOrigin, null, russian ? "RU" : "EU",
    russian ? DIRECT_RU_TIMEOUT_MS : BOOTSTRAP_TIMEOUT_MS,
    selectedCountry
  );
  if (generation !== initializationGeneration) {
    throw new Error("Registration city changed before the route was ready.");
  }
  return applyBootstrapPayload(payload, apiOrigin);
}

export function clearNativeSelectedRegistrationCountry() {
  pendingSelectedCountry = "";
  initializationGeneration += 1;
}

export async function initializeNativeRegionRouting({ accessToken = null } = {}) {
  if (!isNativeApp()) return getNativeRoutingSnapshot();

  const restored = restoreStoredSnapshot();
  const generation = ++initializationGeneration;
  const applyCurrentBootstrap = (payload, apiOrigin) => generation === initializationGeneration
    ? applyBootstrapPayload(payload, apiOrigin)
    : getNativeRoutingSnapshot();
  const primaryResult = settledBootstrap(
    requestValidBootstrap(NATIVE_API_BASE_URL, accessToken, BOOTSTRAP_TIMEOUT_MS, "EU"),
    NATIVE_API_BASE_URL
  );
  let directResult = null;
  const startDirectProbe = () => {
    if (!directResult) {
      directResult = settledBootstrap(
        requestValidBootstrap(RU_NATIVE_API_BASE_URL, accessToken, DIRECT_RU_TIMEOUT_MS, "EU"),
        RU_NATIVE_API_BASE_URL
      );
    }
    return directResult;
  };

  const first = await Promise.race([
    primaryResult,
    new Promise((resolve) => setTimeout(() => resolve({ hedge: true }), PRIMARY_HEDGE_DELAY_MS)),
  ]);

  if (!first.hedge) {
    if (first.ok) {
      const snapshot = applyCurrentBootstrap(first.payload, first.apiOrigin);
      if (!snapshot.networkCountry) {
        promoteBackgroundRuProbe(startDirectProbe(), generation);
      }
      return snapshot;
    }

    const direct = await startDirectProbe();
    return direct.ok
      ? applyCurrentBootstrap(direct.payload, direct.apiOrigin)
      : restored || getNativeRoutingSnapshot();
  }

  const direct = startDirectProbe();
  const winner = await Promise.race([primaryResult, direct]);

  if (winner.apiOrigin === NATIVE_API_BASE_URL) {
    if (winner.ok) {
      const snapshot = applyCurrentBootstrap(winner.payload, winner.apiOrigin);
      if (!snapshot.networkCountry) promoteBackgroundRuProbe(direct, generation);
      return snapshot;
    }
    const directAfterFailure = await direct;
    return directAfterFailure.ok
      ? applyCurrentBootstrap(directAfterFailure.payload, directAfterFailure.apiOrigin)
      : restored || getNativeRoutingSnapshot();
  }

  if (winner.ok) {
    const directSnapshot = normalizeBootstrapPayload(winner.payload, winner.apiOrigin);
    if (hasRussianRoutingSignal(directSnapshot)) {
      return applyCurrentBootstrap(winner.payload, winner.apiOrigin);
    }
  }

  const primaryAfterDirect = await primaryResult;
  if (primaryAfterDirect.ok) {
    return applyCurrentBootstrap(primaryAfterDirect.payload, primaryAfterDirect.apiOrigin);
  }
  return winner.ok
    ? applyCurrentBootstrap(winner.payload, winner.apiOrigin)
    : restored || getNativeRoutingSnapshot();
}
